// ============================================================================
// Europe/London time helpers — SINGLE SOURCE OF TRUTH.
// Ported from design-reference/src/data.jsx + dashboard.jsx, with ONE deliberate
// correctness fix: the prototype hardcodes LDN_OFFSET = "+01:00" (BST) because
// its seed window is all summer. Production spans GMT/BST, so the wall-clock ->
// UTC conversion (`ldnCompose`, `londonTime`, `relativeTime`) is replaced with a
// DST-aware resolver. The display formatters already used Intl correctly and
// port as-is. Everything is epoch ms in / epoch ms out; all calendar reasoning
// is Europe/London. Weeks start Monday.
// ============================================================================
import { TIMEZONE } from './constants.js';

export const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
export const DAY_MS = 86_400_000;
export const pad = (n: number): string => String(n).padStart(2, '0');

// ── DST-aware London <-> UTC core ───────────────────────────────────────────

const _numPartsFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

interface LondonParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function londonNumParts(ms: number): LondonParts {
  const out: Record<string, string> = {};
  for (const p of _numPartsFmt.formatToParts(ms)) {
    if (p.type !== 'literal') out[p.type] = p.value;
  }
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour),
    minute: Number(out.minute),
    second: Number(out.second),
  };
}

/** London wall-clock at instant `ms`, expressed as a UTC epoch (for offset math). */
function londonWallAsUtc(ms: number): number {
  const p = londonNumParts(ms);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/**
 * Given a Europe/London wall-clock time, return the correct UTC epoch ms,
 * DST-aware (GMT in winter, BST in summer). Converges in 1–2 iterations; the
 * loop also resolves the rare spring-forward gap / autumn fall-back overlap.
 */
export function londonWallToUtcMs(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
): number {
  const target = Date.UTC(year, month - 1, day, hour, minute, second);
  let utc = target;
  for (let i = 0; i < 4; i++) {
    const offset = londonWallAsUtc(utc) - utc; // London offset at this instant
    const next = target - offset;
    if (next === utc) break;
    utc = next;
  }
  return utc;
}

// ── date strings / inputs ───────────────────────────────────────────────────

const _dateStrFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** London calendar date "YYYY-MM-DD" for N days before `base`. */
export function londonDateStr(daysAgo = 0, base: number = Date.now()): string {
  return _dateStrFmt.format(base - daysAgo * DAY_MS);
}

/** Epoch ms for a London wall-clock time (h:m) on the date N days before `base`. */
export function londonTime(daysAgo: number, h: number, m: number, base: number = Date.now()): number {
  return ldnCompose(londonDateStr(daysAgo, base), `${pad(h)}:${pad(m)}`);
}

/** Compose a London date "YYYY-MM-DD" + time "HH:MM" into epoch ms (DST-aware). */
export function ldnCompose(dateStr: string, timeStr: string): number {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = (timeStr || '00:00').split(':').map(Number);
  return londonWallToUtcMs(y, mo, d, h || 0, mi || 0);
}

export const ldnDateInput = (ms: number): string => londonDateStr(0, ms); // "YYYY-MM-DD"
export const ldnTimeInput = (ms: number): string => fmtTime(ms); // "HH:MM"

// ── display formatters (Intl, locale-independent output) ─────────────────────

const _partsFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function _parts(ms: number): Record<string, string> {
  return _partsFmt.formatToParts(ms).reduce<Record<string, string>>((o, p) => {
    o[p.type] = p.value;
    return o;
  }, {});
}

export function fmtDateTime(ms: number): string {
  const p = _parts(ms);
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute}`;
}
export function fmtDate(ms: number): string {
  const p = _parts(ms);
  return `${p.day} ${p.month} ${p.year}`;
}
export function fmtTime(ms: number): string {
  const p = _parts(ms);
  return `${p.hour}:${p.minute}`;
}

/** "HH:MM:SS" for live timers. */
export function fmtHMS(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/** "1h 23m" / "45m" for durations in lists. */
export function fmtDur(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${pad(m)}m` : `${m}m`;
}

/** Decimal hours, Clockify-style (e.g. "1.99"). */
export const fmtDec = (ms: number): string => (Math.max(0, ms) / 3_600_000).toFixed(2);

/** Raw decimal hours (unformatted). */
export const hours = (ms: number): number => ms / 3_600_000;

/** Relative "last clocked" stamp. */
export function relativeTime(ms: number, now: number = Date.now()): string {
  const diff = now - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const todayNoon = ldnCompose(londonDateStr(0, now), '12:00');
  const thatNoon = ldnCompose(londonDateStr(0, ms), '12:00');
  const days = Math.round((todayNoon - thatNoon) / DAY_MS);
  if (days === 0) {
    const h = Math.floor(mins / 60);
    return `${h}h ago`;
  }
  if (days === 1) return `Yesterday ${fmtTime(ms)}`;
  return `${days} days ago`;
}

// ── weeks (Monday, London) ────────────────────────────────────────────────────

/** The Monday (as "YYYY-MM-DD") of the London week containing `dayKey`. */
export function mondayKeyOf(dayKey: string): string {
  const ms = ldnCompose(dayKey, '12:00');
  const dow = (new Date(ms).getUTCDay() + 6) % 7; // 0 = Monday
  return londonDateStr(0, ms - dow * DAY_MS);
}

/** ISO-8601 week number (W1–W53) of the London calendar date at `ms`. */
export function isoWeek(ms: number): number {
  const [y, mo, d] = londonDateStr(0, ms).split('-').map(Number);
  const date = new Date(Date.UTC(y, mo - 1, d));
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon = 0
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // Thursday of this week
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const ftDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ftDayNum + 3);
  return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
}

// ── calendar grid (Monday-start, London) — for the custom range picker ───────

/** Day-of-week of a London date key, 0 = Monday … 6 = Sunday. */
export function dowMon(dayKey: string): number {
  return (new Date(ldnCompose(dayKey, '12:00')).getUTCDay() + 6) % 7;
}

/** London date key `n` days after `dayKey` (DST-safe; anchored at noon). */
export function addDays(dayKey: string, n: number): string {
  return londonDateStr(0, ldnCompose(dayKey, '12:00') + n * DAY_MS);
}

/** A 1-based {year, month} shifted by `delta` months, wrapping years. */
export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const idx = year * 12 + (month - 1) + delta;
  return { year: Math.floor(idx / 12), month: (idx % 12) + 1 };
}

export interface CalendarCell {
  key: string; // "YYYY-MM-DD"
  day: number; // 1–31
  inMonth: boolean; // belongs to the requested (year, month)
}

/**
 * A fixed 6-week (42-cell) Monday-start grid for the 1-based (year, month).
 * Leading/trailing cells are filled from the adjacent months (inMonth=false).
 */
export function monthGrid(year: number, month: number): CalendarCell[] {
  const first = `${year}-${pad(month)}-01`;
  const start = addDays(first, -dowMon(first)); // back up to that week's Monday
  return Array.from({ length: 42 }, (_, i) => {
    const key = addDays(start, i);
    const [, mo, d] = key.split('-').map(Number);
    return { key, day: d, inMonth: mo === month };
  });
}

// ── date ranges (Europe/London) — ported from dashboard.jsx getRange ─────────

export type RangePreset =
  | 'today'
  | 'yesterday'
  | 'week'
  | 'lastweek'
  | 'last2weeks'
  | 'month'
  | 'lastmonth'
  | 'year'
  | 'custom';

export interface DateRange {
  from: number;
  to: number;
}

export const RANGE_PRESETS: [RangePreset, string][] = [
  ['today', 'Today'],
  ['yesterday', 'Yesterday'],
  ['week', 'This week'],
  ['lastweek', 'Last week'],
  ['last2weeks', 'Past two weeks'],
  ['month', 'This month'],
  ['lastmonth', 'Last month'],
  ['year', 'This year'],
];

/**
 * Half-open [from, to) range for a preset, with `offset` shifting the whole
 * range by its own span (the ‹ › steppers). Calendar-aware for months/years.
 */
export function getRange(
  preset: RangePreset,
  now: number,
  customFrom?: string,
  customTo?: string,
  offset = 0,
): DateRange {
  const D = DAY_MS;
  const mondayMs = (): number => {
    const dow = new Date(londonTime(0, 12, 0, now)).getUTCDay(); // 0 = Sun
    const back = (dow + 6) % 7; // days since Monday
    return londonTime(back, 0, 0, now);
  };
  const monthStart = (delta = 0): number => {
    const ds = londonDateStr(0, now);
    let [y, m] = ds.split('-').map(Number);
    m += delta;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    return ldnCompose(`${y}-${pad(m)}-01`, '00:00');
  };
  if (preset === 'today') {
    const f = londonTime(0, 0, 0, now) + offset * D;
    return { from: f, to: f + D };
  }
  if (preset === 'yesterday') {
    const f = londonTime(1, 0, 0, now) + offset * D;
    return { from: f, to: f + D };
  }
  if (preset === 'week') {
    const f = mondayMs() + offset * 7 * D;
    return { from: f, to: f + 7 * D };
  }
  if (preset === 'lastweek') {
    const f = mondayMs() + (offset - 1) * 7 * D;
    return { from: f, to: f + 7 * D };
  }
  if (preset === 'last2weeks') {
    const f = mondayMs() + (offset - 1) * 14 * D;
    return { from: f, to: f + 14 * D };
  }
  if (preset === 'month') return { from: monthStart(offset), to: monthStart(offset + 1) };
  if (preset === 'lastmonth') return { from: monthStart(offset - 1), to: monthStart(offset) };
  if (preset === 'year') {
    const y = Number(londonDateStr(0, now).slice(0, 4)) + offset;
    return { from: ldnCompose(`${y}-01-01`, '00:00'), to: ldnCompose(`${y + 1}-01-01`, '00:00') };
  }
  // custom (inclusive of the end date)
  const f0 = ldnCompose(customFrom ?? londonDateStr(0, now), '00:00');
  const t0 = ldnCompose(customTo ?? londonDateStr(0, now), '00:00') + D;
  const span = Math.max(t0 - f0, D);
  return { from: f0 + offset * span, to: t0 + offset * span };
}

const _rangeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const _rangeFmtShort = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  day: 'numeric',
  month: 'short',
});

export function rangeLabel(preset: RangePreset, offset: number, range: DateRange): string {
  if (offset === 0 && preset !== 'custom') {
    return RANGE_PRESETS.find(([id]) => id === preset)?.[1] ?? preset;
  }
  const a = range.from;
  const b = range.to - 1;
  if (range.to - range.from <= DAY_MS) return _rangeFmt.format(a);
  return `${_rangeFmtShort.format(a)} – ${_rangeFmt.format(b)}`;
}

// ── week/day grouping for the Tracker entry list ─────────────────────────────

const _fmtDayMon = new Intl.DateTimeFormat('en-GB', { timeZone: TIMEZONE, day: 'numeric', month: 'short' });
const _fmtWkday = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

export interface DayGroup<E> {
  dayKey: string;
  label: string;
  totalMs: number;
  entries: E[];
}
export interface WeekGroup<E> {
  mondayKey: string;
  label: string;
  totalMs: number;
  days: DayGroup<E>[];
}

/**
 * Group entries into week sections (newest first), each with day cards. Totals
 * use (stop ?? now) so running entries grow live. Ported from tracker.jsx.
 */
export function buildWeekGroups<E extends { start: number; stop: number | null }>(
  entries: E[],
  now: number = Date.now(),
): WeekGroup<E>[] {
  const mine = [...entries].sort((a, b) => b.start - a.start);

  const dayMap = new Map<string, E[]>();
  for (const e of mine) {
    const k = ldnDateInput(e.start);
    if (!dayMap.has(k)) dayMap.set(k, []);
    dayMap.get(k)!.push(e);
  }

  const weekMap = new Map<string, { dayKey: string; entries: E[] }[]>();
  [...dayMap.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .forEach(([dayKey, list]) => {
      const mk = mondayKeyOf(dayKey);
      if (!weekMap.has(mk)) weekMap.set(mk, []);
      weekMap.get(mk)!.push({ dayKey, entries: list });
    });

  const todayKey = londonDateStr(0, now);
  const yesterdayKey = londonDateStr(1, now);
  const thisMonday = mondayKeyOf(todayKey);
  const lastMonday = londonDateStr(0, ldnCompose(thisMonday, '12:00') - 7 * DAY_MS);

  const weekLabel = (mk: string): string => {
    if (mk === thisMonday) return 'This week';
    if (mk === lastMonday) return 'Last week';
    const ms = ldnCompose(mk, '12:00');
    return `${_fmtDayMon.format(ms)} – ${_fmtDayMon.format(ms + 6 * DAY_MS)}`;
  };
  const dayLabel = (dk: string): string => {
    if (dk === todayKey) return 'Today';
    if (dk === yesterdayKey) return 'Yesterday';
    return _fmtWkday.format(ldnCompose(dk, '12:00'));
  };
  const sumOf = (es: E[]): number => es.reduce((s, e) => s + ((e.stop ?? now) - e.start), 0);

  return [...weekMap.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([mondayKey, days]) => ({
      mondayKey,
      label: weekLabel(mondayKey),
      totalMs: sumOf(days.flatMap((d) => d.entries)),
      days: days.map((d) => ({
        dayKey: d.dayKey,
        label: dayLabel(d.dayKey),
        totalMs: sumOf(d.entries),
        entries: d.entries,
      })),
    }));
}

// ── same-entry grouping (Clockify-style) ─────────────────────────────────────

export interface EntryGroup<E> {
  /** Stable grouping key (description + project + task), or unique for a running entry. */
  key: string;
  /** Members in the input order (newest-first when fed a day's entries). */
  entries: E[];
  /** Newest member — used to display the shared description/project/task. */
  sample: E;
  /** Sum of every member's duration, live-aware (stop ?? now). */
  totalMs: number;
  /** Earliest start across members. */
  start: number;
  /** Latest stop across members, or null if a member is still running. */
  stop: number | null;
  /** True if any member is running. */
  running: boolean;
}

/**
 * Collapse a day's entries into groups of identical work — same description +
 * project + task — Clockify-style. A running entry (stop === null) is never
 * merged; it stays on its own. Group order follows each group's newest member,
 * so feed entries newest-first (as buildWeekGroups produces).
 */
export function groupSameEntries<
  E extends { start: number; stop: number | null; description: string; projectId: string | null; taskId: string | null },
>(entries: E[], now: number = Date.now()): EntryGroup<E>[] {
  const map = new Map<string, EntryGroup<E>>();
  let runningSeq = 0;
  for (const e of entries) {
    const isRunning = e.stop === null;
    // JSON.stringify gives a canonical, collision-free key; running entries get a
    // unique "run:N" key (a different shape) so they are never merged.
    const key = isRunning ? `run:${runningSeq++}` : JSON.stringify([e.description, e.projectId, e.taskId]);
    const dur = (e.stop ?? now) - e.start;
    const g = map.get(key);
    if (!g) {
      map.set(key, { key, entries: [e], sample: e, totalMs: dur, start: e.start, stop: e.stop, running: isRunning });
    } else {
      g.entries.push(e);
      g.totalMs += dur;
      g.start = Math.min(g.start, e.start);
      g.stop = g.stop === null || isRunning ? null : Math.max(g.stop, e.stop as number);
      g.running = g.running || isRunning;
    }
  }
  return [...map.values()];
}
